// ─── Canonical Part-Type Taxonomy ─────────────────────────────
// Single source of truth for all unit/part classification.
// Tiers: 'vehicle' (full cars), 'cut' (dismantled sections), 'part' (individual components).
// `cuttable` means sub-parts can be requested from this item.
// `group` clusters entries for dropdown UI grouping.

export const PART_TYPES = [
  // ── Tier 1: Full Vehicle ──
  { id: 'full_vehicle', display: 'Full Vehicle', tier: 'vehicle', cuttable: true, group: 'Vehicle', vehicleClasses: ['car', 'truck', 'bus', 'machinery'] },

  // ── Tier 2: Cuts ──
  { id: 'half_cut', display: 'Half Cut', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck'] },
  { id: 'half_cut_front', display: 'Half Cut (Front)', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck'] },
  { id: 'half_cut_rear', display: 'Half Cut (Rear)', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck'] },
  { id: 'nose_cut', display: 'Nose Cut', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck'] },
  { id: 'front_cut', display: 'Front Cut', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck'] },
  { id: 'rear_cut', display: 'Rear Cut', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck'] },
  { id: 'roof_cut', display: 'Roof Cut', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck'] },
  { id: 'ckd', display: 'CKD', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'skd', display: 'SKD', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'cbu', display: 'CBU', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'body_shell', display: 'Body Shell', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'truck_cabin', display: 'Truck Cabin', tier: 'cut', cuttable: true, group: 'Cut Types', vehicleClasses: ['truck'] },

  // ── Tier 3: Parts — Powertrain ──
  { id: 'engine', display: 'Engine', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'engine_transmission', display: 'Engine + Transmission', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'transmission', display: 'Transmission', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'turbocharger', display: 'Turbocharger', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck'] },
  { id: 'intercooler', display: 'Intercooler', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck'] },
  { id: 'alternator', display: 'Alternator', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'starter_motor', display: 'Starter Motor', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'clutch', display: 'Clutch', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck'] },
  { id: 'flywheel', display: 'Flywheel', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck'] },
  { id: 'throttle_body', display: 'Throttle Body', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck'] },
  { id: 'ignition_distributor', display: 'Ignition Distributor', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car'] },
  { id: 'air_cleaner', display: 'Air Cleaner', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'cylinder_head', display: 'Cylinder Head', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck'] },
  { id: 'power_steering_pump', display: 'Power Steering Pump', tier: 'part', cuttable: false, group: 'Powertrain', vehicleClasses: ['car', 'truck', 'bus'] },

  // ── Tier 3: Parts — Fuel System ──
  { id: 'fuel_pump', display: 'Fuel Pump', tier: 'part', cuttable: false, group: 'Fuel System', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'fuel_injector', display: 'Fuel Injector', tier: 'part', cuttable: false, group: 'Fuel System', vehicleClasses: ['car', 'truck'] },
  { id: 'fuel_tank', display: 'Fuel Tank', tier: 'part', cuttable: false, group: 'Fuel System', vehicleClasses: ['car', 'truck', 'bus'] },

  // ── Tier 3: Parts — Exhaust ──
  { id: 'exhaust', display: 'Exhaust', tier: 'part', cuttable: false, group: 'Exhaust', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'muffler', display: 'Muffler', tier: 'part', cuttable: false, group: 'Exhaust', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'catalytic_converter', display: 'Catalytic Converter', tier: 'part', cuttable: false, group: 'Exhaust', vehicleClasses: ['car', 'truck'] },

  // ── Tier 3: Parts — Drivetrain ──
  { id: 'axle', display: 'Axle', tier: 'part', cuttable: false, group: 'Drivetrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'front_axle', display: 'Front Axle', tier: 'part', cuttable: false, group: 'Drivetrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'rear_axle', display: 'Rear Axle', tier: 'part', cuttable: false, group: 'Drivetrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'differential', display: 'Differential', tier: 'part', cuttable: false, group: 'Drivetrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'drive_shaft', display: 'Drive Shaft', tier: 'part', cuttable: false, group: 'Drivetrain', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'transfer_case', display: 'Transfer Case', tier: 'part', cuttable: false, group: 'Drivetrain', vehicleClasses: ['car', 'truck'] },
  { id: 'propeller_shaft', display: 'Propeller Shaft', tier: 'part', cuttable: false, group: 'Drivetrain', vehicleClasses: ['car', 'truck'] },

  // ── Tier 3: Parts — Suspension & Steering ──
  { id: 'suspension', display: 'Suspension', tier: 'part', cuttable: false, group: 'Suspension & Steering', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'shock_absorber', display: 'Shock Absorber', tier: 'part', cuttable: false, group: 'Suspension & Steering', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'coil_spring', display: 'Coil Spring', tier: 'part', cuttable: false, group: 'Suspension & Steering', vehicleClasses: ['car', 'truck'] },
  { id: 'control_arm', display: 'Control Arm', tier: 'part', cuttable: false, group: 'Suspension & Steering', vehicleClasses: ['car', 'truck'] },
  { id: 'steering_rack', display: 'Steering Rack', tier: 'part', cuttable: false, group: 'Suspension & Steering', vehicleClasses: ['car', 'truck'] },
  { id: 'steering_column', display: 'Steering Column', tier: 'part', cuttable: false, group: 'Suspension & Steering', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'steering_gearbox', display: 'Steering Gearbox', tier: 'part', cuttable: false, group: 'Suspension & Steering', vehicleClasses: ['truck', 'bus'] },

  // ── Tier 3: Parts — Body ──
  { id: 'door', display: 'Door', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'door_front', display: 'Front Door', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car'] },
  { id: 'door_rear', display: 'Rear Door', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car'] },
  { id: 'bumper', display: 'Bumper', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'bumper_front', display: 'Front Bumper', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'bumper_rear', display: 'Rear Bumper', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'fender', display: 'Fender', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'grille', display: 'Grille', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'bonnet', display: 'Bonnet / Hood', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'boot_lid', display: 'Boot Lid / Trunk', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car'] },
  { id: 'tailgate', display: 'Tailgate', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car', 'truck'] },
  { id: 'roof_panel', display: 'Roof Panel', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car'] },
  { id: 'quarter_panel', display: 'Quarter Panel', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car'] },
  { id: 'spoiler', display: 'Spoiler', tier: 'part', cuttable: false, group: 'Body', vehicleClasses: ['car'] },

  // ── Tier 3: Parts — Glass ──
  { id: 'glass', display: 'Glass', tier: 'part', cuttable: false, group: 'Glass', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'windscreen', display: 'Windscreen', tier: 'part', cuttable: false, group: 'Glass', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'door_glass', display: 'Door Glass', tier: 'part', cuttable: false, group: 'Glass', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'rear_glass', display: 'Rear Glass', tier: 'part', cuttable: false, group: 'Glass', vehicleClasses: ['car', 'truck', 'bus'] },

  // ── Tier 3: Parts — Lights ──
  { id: 'lights', display: 'Lights', tier: 'part', cuttable: false, group: 'Lights', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'headlight', display: 'Headlight', tier: 'part', cuttable: false, group: 'Lights', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'tail_light', display: 'Tail Light', tier: 'part', cuttable: false, group: 'Lights', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'fog_light', display: 'Fog Light', tier: 'part', cuttable: false, group: 'Lights', vehicleClasses: ['car', 'truck'] },

  // ── Tier 3: Parts — Interior ──
  { id: 'dashboard', display: 'Dashboard', tier: 'part', cuttable: false, group: 'Interior', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'seats', display: 'Seats', tier: 'part', cuttable: false, group: 'Interior', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'seat_front', display: 'Front Seat', tier: 'part', cuttable: false, group: 'Interior', vehicleClasses: ['car'] },
  { id: 'seat_rear', display: 'Rear Seat', tier: 'part', cuttable: false, group: 'Interior', vehicleClasses: ['car'] },
  { id: 'inside_panel', display: 'Inside Panel', tier: 'part', cuttable: false, group: 'Interior', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'steering_wheel', display: 'Steering Wheel', tier: 'part', cuttable: false, group: 'Interior', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'instrument_cluster', display: 'Instrument Cluster', tier: 'part', cuttable: false, group: 'Interior', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'airbag', display: 'Airbag', tier: 'part', cuttable: false, group: 'Interior', vehicleClasses: ['car'] },
  { id: 'seatbelt', display: 'Seatbelt', tier: 'part', cuttable: false, group: 'Interior', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'heater_blower', display: 'Heater Blower', tier: 'part', cuttable: false, group: 'Interior', vehicleClasses: ['car', 'truck', 'bus'] },

  // ── Tier 3: Parts — Brakes ──
  { id: 'brake_pads', display: 'Brake Pads', tier: 'part', cuttable: false, group: 'Brakes', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'brake_disc', display: 'Brake Disc / Rotor', tier: 'part', cuttable: false, group: 'Brakes', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'brake_caliper', display: 'Brake Caliper', tier: 'part', cuttable: false, group: 'Brakes', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'abs_unit', display: 'ABS Unit', tier: 'part', cuttable: false, group: 'Brakes', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'brake_booster', display: 'Brake Booster', tier: 'part', cuttable: false, group: 'Brakes', vehicleClasses: ['car', 'truck', 'bus'] },

  // ── Tier 3: Parts — Cooling & AC ──
  { id: 'radiator', display: 'Radiator', tier: 'part', cuttable: false, group: 'Cooling & AC', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'ac_compressor', display: 'AC Compressor', tier: 'part', cuttable: false, group: 'Cooling & AC', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'ac_condenser', display: 'AC Condenser', tier: 'part', cuttable: false, group: 'Cooling & AC', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'ac_assembly', display: 'AC Assembly', tier: 'part', cuttable: false, group: 'Cooling & AC', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'radiator_fan', display: 'Radiator Fan', tier: 'part', cuttable: false, group: 'Cooling & AC', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'heater_core', display: 'Heater Core', tier: 'part', cuttable: false, group: 'Cooling & AC', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'oil_cooler', display: 'Oil Cooler', tier: 'part', cuttable: false, group: 'Cooling & AC', vehicleClasses: ['car', 'truck'] },

  // ── Tier 3: Parts — Electrical ──
  { id: 'ecu', display: 'ECU', tier: 'part', cuttable: false, group: 'Electrical', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'wiring_harness', display: 'Wiring Harness', tier: 'part', cuttable: false, group: 'Electrical', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'electric_parts', display: 'Electric Parts', tier: 'part', cuttable: false, group: 'Electrical', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'battery', display: 'Battery', tier: 'part', cuttable: false, group: 'Electrical', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'abs_computer', display: 'ABS Computer', tier: 'part', cuttable: false, group: 'Electrical', vehicleClasses: ['car', 'truck'] },
  { id: 'sensor', display: 'Sensor', tier: 'part', cuttable: false, group: 'Electrical', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'wiper_motor', display: 'Wiper Motor', tier: 'part', cuttable: false, group: 'Electrical', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'window_motor', display: 'Window Motor', tier: 'part', cuttable: false, group: 'Electrical', vehicleClasses: ['car'] },
  { id: 'relay_switch', display: 'Relay / Switch', tier: 'part', cuttable: false, group: 'Electrical', vehicleClasses: ['car', 'truck', 'bus'] },

  // ── Tier 3: Parts — Exterior ──
  { id: 'mirror', display: 'Mirror', tier: 'part', cuttable: false, group: 'Exterior', vehicleClasses: ['car', 'truck', 'bus'] },

  // ── Tier 3: Parts — Wheels & Tyres ──
  { id: 'wheels', display: 'Wheels', tier: 'part', cuttable: false, group: 'Wheels & Tyres', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'tyres', display: 'Tyres', tier: 'part', cuttable: false, group: 'Wheels & Tyres', vehicleClasses: ['car', 'truck', 'bus'] },
  { id: 'wheel_tyre_set', display: 'Wheel + Tyre Set', tier: 'part', cuttable: false, group: 'Wheels & Tyres', vehicleClasses: ['car', 'truck'] },
  { id: 'hubcap', display: 'Hubcap', tier: 'part', cuttable: false, group: 'Wheels & Tyres', vehicleClasses: ['car'] },

  // ── Truck & Bus Parts ──
  { id: 'truck_chassis', display: 'Truck Chassis', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'truck_differential', display: 'Truck Differential', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'truck_axle', display: 'Truck Axle', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'truck_transmission', display: 'Truck Transmission', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'truck_engine', display: 'Truck Engine', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'truck_body', display: 'Truck Body', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'truck_bumper', display: 'Truck Bumper', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'truck_grille', display: 'Truck Grille', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'truck_door', display: 'Truck Door', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'truck_leaf_spring', display: 'Truck Leaf Spring', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'truck_fifth_wheel', display: 'Fifth Wheel Coupling', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'pto', display: 'PTO (Power Take-Off)', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck', 'machinery'] },
  { id: 'winch', display: 'Winch', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck', 'machinery'] },
  { id: 'crane_unit', display: 'Crane Unit', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['truck'] },
  { id: 'bus_body', display: 'Bus Body', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['bus'] },
  { id: 'bus_seats', display: 'Bus Seats', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['bus'] },
  { id: 'bus_door_mechanism', display: 'Bus Door Mechanism', tier: 'part', cuttable: false, group: 'Truck & Bus', vehicleClasses: ['bus'] },

  // ── Machinery — Whole Units ──
  { id: 'excavator', display: 'Excavator', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'bulldozer', display: 'Bulldozer', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'wheel_loader', display: 'Wheel Loader', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'backhoe_loader', display: 'Backhoe Loader', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'skid_steer', display: 'Skid Steer', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'motor_grader', display: 'Motor Grader', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'road_roller', display: 'Road Roller', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'crane', display: 'Crane', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'forklift', display: 'Forklift', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'dump_truck_offroad', display: 'Off-Road Dump Truck', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'crawler_carrier', display: 'Crawler Carrier', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'aerial_platform', display: 'Aerial Platform', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'telehandler', display: 'Telehandler', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'paver', display: 'Paver', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'crusher_machine', display: 'Crusher', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'drilling_machine', display: 'Drilling Machine', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'generator', display: 'Generator', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'welder', display: 'Welder', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'air_compressor', display: 'Air Compressor', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'tractor_agri', display: 'Agricultural Tractor', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },
  { id: 'other_machinery', display: 'Other Machinery', tier: 'vehicle', cuttable: false, group: 'Machinery', vehicleClasses: ['machinery'] },

  // ── Machinery — Parts & Attachments ──
  { id: 'machinery_engine', display: 'Machinery Engine', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'hydraulic_pump', display: 'Hydraulic Pump', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'hydraulic_cylinder', display: 'Hydraulic Cylinder', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'hydraulic_motor', display: 'Hydraulic Motor', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'undercarriage', display: 'Undercarriage', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'track_shoe', display: 'Track Shoe', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'idler_sprocket', display: 'Idler / Sprocket', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'bucket', display: 'Bucket', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'grapple', display: 'Grapple', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'breaker', display: 'Breaker', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'quick_hitch', display: 'Quick Hitch', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'blade', display: 'Blade', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'ripper', display: 'Ripper', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'boom_arm', display: 'Boom / Arm', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'machinery_cabin', display: 'Machinery Cabin', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },
  { id: 'machinery_tyre', display: 'Machinery Tyre', tier: 'part', cuttable: false, group: 'Machinery Parts', vehicleClasses: ['machinery'] },

  // ── Other ──
  { id: 'other', display: 'Other', tier: 'part', cuttable: false, group: 'Other', vehicleClasses: ['car', 'truck', 'bus', 'machinery'] },
];

// ── Lookup indexes ───────────────────────────────────────────
const _byId = new Map(PART_TYPES.map(pt => [pt.id, pt]));
const _byDisplay = new Map(PART_TYPES.map(pt => [pt.display.toLowerCase(), pt]));

export const getPartType = (id) => _byId.get(id) || null;
export const getPartTypeByDisplay = (display) => _byDisplay.get((display || '').toLowerCase()) || null;


// ── Old cutType ID → new canonical ID ────────────────────────
const OLD_CUT_TYPE_MAP = {
  'half-cut':       'half_cut',
  'nose-cut':       'nose_cut',
  'ckd':            'ckd',
  'roof-cut':       'roof_cut',
  'skd':            'skd',
  'cbu':            'cbu',
  'rear-cut':       'rear_cut',
  'front-cut':      'front_cut',
  'ckd-wo-engine':  'ckd',
  'full':           'full_vehicle',
};

export const normalizeCutType = (cutType) => OLD_CUT_TYPE_MAP[cutType] || cutType;

// Old cutType ID → display name (replaces both CUT_TYPES_MAP objects)
const OLD_CUT_TYPE_DISPLAY = {};
for (const [oldId, newId] of Object.entries(OLD_CUT_TYPE_MAP)) {
  const pt = _byId.get(newId);
  if (pt) OLD_CUT_TYPE_DISPLAY[oldId] = pt.display;
}


// ── Legacy CUTTABLE_CATEGORIES for old data ──────────────────
const LEGACY_CUTTABLE = new Set([
  'Half Cut', 'Nose Cut', 'CKD', 'SKD', 'Roof Cut', 'CBU',
  'Half Cut (H/CUT)', 'H/CUT W/O Catalyst',
  'CKD W/O Engine', 'CKD W/O Scrap (Frame etc)',
  'CBU (Complete Built Unit)',
  'Front Cut', 'Rear Cut', 'Engine Only',
  'Half Cut (Front)', 'Half Cut (Rear)',
  'Body Shell', 'Truck Cabin',
]);


// ── resolvePartType(unit) ────────────────────────────────────
// Returns the correct DISPLAY string for any unit, old or new.
// Priority: new partType → full vehicle → old cutType → legacy generic → free-text category → engine → fallback.
export function resolvePartType(unit) {
  if (!unit) return 'Part';

  // (a) NEW records: unit.partType present → canonical lookup
  if (unit.partType) {
    const pt = _byId.get(unit.partType);
    if (pt) return pt.display;
    return unit.partType;
  }

  // (b) Full vehicle: has make, no cut/part/engine indicators
  if (unit.make && !unit.cutType && !unit.category && !unit.partName && !unit.engineName) {
    return 'Full Vehicle';
  }

  // (c) OLD cutType machine IDs → display name
  if (unit.cutType && unit.cutType !== 'full' && unit.cutType !== 'custom') {
    return OLD_CUT_TYPE_DISPLAY[unit.cutType] || unit.cutType;
  }
  if (unit.cutType === 'full') return 'Full Vehicle';

  // (d) LEGACY generic values: category === 'Cut Types' or 'Cut Unit'
  if (unit.category === 'Cut Types' || unit.category === 'Cut Unit') {
    if (unit.cutType) {
      return OLD_CUT_TYPE_DISPLAY[unit.cutType] || unit.cutType;
    }
    const afterDash = unit.partName?.match(/—\s*(.+)$/);
    if (afterDash) return afterDash[1].trim();
    return 'Cut Unit';
  }

  // (e) Free-text category → pass through unchanged
  if (unit.category) return unit.category;

  // (f) Engine
  if (unit.engineName) return 'Engine';

  // (g) Consistent final fallback
  return 'Part';
}


// ── resolvePartTypeEntry(unit) ───────────────────────────────
// Returns the PART_TYPES entry {id, display, tier, …} for any unit, old or new.
// Falls back to the 'other' entry when nothing matches.
export function resolvePartTypeEntry(unit) {
  if (!unit) return _byId.get('other');

  if (unit.partType && unit.partType !== 'other') {
    const pt = _byId.get(unit.partType);
    if (pt) return pt;
  }

  if (unit.cutType && unit.cutType !== 'full' && unit.cutType !== 'custom') {
    const pt = _byId.get(normalizeCutType(unit.cutType));
    if (pt) return pt;
  }
  if (unit.cutType === 'full') return _byId.get('full_vehicle');

  if (unit.partName) {
    let pt = _byDisplay.get(unit.partName.toLowerCase());
    if (pt) return pt;
    const afterDash = unit.partName.match(/—\s*(.+)$/);
    if (afterDash) {
      pt = _byDisplay.get(afterDash[1].trim().toLowerCase());
      if (pt) return pt;
    }
    const mapped = OLD_TO_NEW_MAPPING.category[unit.partName];
    if (mapped && mapped !== 'REVIEW') {
      pt = _byId.get(mapped);
      if (pt) return pt;
    }
  }

  if (unit.category && unit.category !== 'Cut Types' && unit.category !== 'Cut Unit') {
    let pt = _byDisplay.get(unit.category.toLowerCase());
    if (pt) return pt;
    const mapped = OLD_TO_NEW_MAPPING.category[unit.category];
    if (mapped && mapped !== 'REVIEW') {
      pt = _byId.get(mapped);
      if (pt) return pt;
    }
  }

  if (unit.make && !unit.cutType && !unit.engineName) {
    return _byId.get('full_vehicle');
  }

  return _byId.get('other');
}


// ── isCuttable(unit) ─────────────────────────────────────────
// Returns true if sub-parts can be requested from this unit.
export function isCuttable(unit) {
  if (!unit) return false;

  // New records: check PART_TYPES.cuttable
  if (unit.partType) {
    const pt = _byId.get(unit.partType);
    return pt ? pt.cuttable : false;
  }

  // Full vehicles are always cuttable
  if (unit.make && !unit.cutType && !unit.category) return true;

  // Old cutType: look up canonical entry
  if (unit.cutType && unit.cutType !== 'custom') {
    const newId = OLD_CUT_TYPE_MAP[unit.cutType];
    if (newId) {
      const pt = _byId.get(newId);
      return pt ? pt.cuttable : false;
    }
    return false;
  }

  // Old category: check legacy set
  if (unit.category && LEGACY_CUTTABLE.has(unit.category)) return true;

  return false;
}


// ── Old → New Mapping Table ──────────────────────────────────
// For data reconciliation. Maps every known old value to a canonical partType id.
// 'REVIEW' means the mapping is ambiguous and needs manual inspection.
export const OLD_TO_NEW_MAPPING = {
  // ── Old cutType IDs (from CUT_TYPES, CUT_TYPES_MAP, handleCustomerAdd) ──
  cutType: {
    'half-cut':       'half_cut',
    'nose-cut':       'nose_cut',
    'ckd':            'ckd',
    'roof-cut':       'roof_cut',
    'skd':            'skd',
    'cbu':            'cbu',
    'rear-cut':       'rear_cut',
    'front-cut':      'front_cut',
    'ckd-wo-engine':  'ckd',
    'full':           'full_vehicle',
    'custom':         'REVIEW',
  },

  // ── Old category display names ──
  category: {
    // From CUT_TYPES.find()?.name resolution (5 core)
    'Half Cut':                        'half_cut',
    'Nose Cut':                        'nose_cut',
    'CKD':                             'ckd',
    'Roof Cut':                        'roof_cut',
    'SKD':                             'skd',

    // From ALL_CUT_TYPES extras (CutPlans.jsx)
    'Rear Cut':                        'rear_cut',
    'Front Cut':                       'front_cut',
    'Engine Only':                     'engine',

    // From PARTS_CATALOG "Cut Types" group
    'CBU (Complete Built Unit)':       'cbu',
    'Half Cut (H/CUT)':               'half_cut',
    'H/CUT W/O Catalyst':             'half_cut',
    'CKD W/O Scrap (Frame etc)':      'ckd',
    'CKD W/O Engine':                 'ckd',
    'CKD W/O Engine W/O Catalyst':    'ckd',
    'CKD W/O Catalyst (All) W/O Scrap': 'ckd',

    // From CUT_TYPES_MAP in generateInvoicePDF.js
    'CBU':                             'cbu',
    'CKD W/O Engine':                  'ckd',

    // From BulkPartsUpload PART_TYPES dropdown (25 entries)
    'Engine':                          'engine',
    'Door':                            'door',
    'Bumper':                          'bumper',
    'Axle':                            'axle',
    'Transmission':                    'transmission',
    'Lights':                          'lights',
    'Suspension':                      'suspension',
    'Dashboard':                       'dashboard',
    'Seats':                           'seats',
    'Mirrors':                         'mirror',
    'Radiator':                        'radiator',
    'AC Compressor':                   'ac_compressor',
    'Steering Rack':                   'steering_rack',
    'Brake Pads':                      'brake_pads',
    'Differential':                    'differential',
    'Drive Shaft':                     'drive_shaft',
    'Fuel pump':                       'fuel_pump',
    'Fuel Pump':                       'fuel_pump',
    'Grille':                          'grille',

    // Taxonomy expansion — new canonical types
    'Turbo':                           'turbocharger',
    'Turbocharger':                    'turbocharger',
    'Intercooler':                     'intercooler',
    'Alternator':                      'alternator',
    'Starter Motor':                   'starter_motor',
    'Starter':                         'starter_motor',
    'Clutch':                          'clutch',
    'Flywheel':                        'flywheel',
    'Throttle Body':                   'throttle_body',
    'Air Cleaner':                     'air_cleaner',
    'Air Filter':                      'air_cleaner',
    'Cylinder Head':                   'cylinder_head',
    'Power Steering Pump':             'power_steering_pump',
    'Fuel Injector':                   'fuel_injector',
    'Fuel Tank':                       'fuel_tank',
    'Exhaust':                         'exhaust',
    'Muffler':                         'muffler',
    'Catalytic Converter':             'catalytic_converter',
    'Transfer case':                   'transfer_case',
    'Transfer Case':                   'transfer_case',
    'Propeller shaft':                 'propeller_shaft',
    'Propeller Shaft':                 'propeller_shaft',
    'Shock absorber':                  'shock_absorber',
    'Shock Absorber':                  'shock_absorber',
    'Coil Spring':                     'coil_spring',
    'Control Arm':                     'control_arm',
    'Steering Column':                 'steering_column',
    'Steering Gearbox':                'steering_gearbox',
    'AC Condenser':                    'ac_condenser',
    'Radiator Fan':                    'radiator_fan',
    'Heater Core':                     'heater_core',
    'Oil Cooler':                      'oil_cooler',
    'Tailgate':                        'tailgate',
    'Spoiler':                         'spoiler',
    'Windscreen':                      'windscreen',
    'Windshield':                      'windscreen',
    'Fog Light':                       'fog_light',
    'Steering Wheel':                  'steering_wheel',
    'Instrument Cluster':              'instrument_cluster',
    'Airbag':                          'airbag',
    'Seatbelt':                        'seatbelt',
    'Battery':                         'battery',
    'ABS Unit':                        'abs_unit',
    'ABS Computer':                    'abs_computer',
    'Brake Booster':                   'brake_booster',
    'Wiper Motor':                     'wiper_motor',
    'Window Motor':                    'window_motor',
    'Hydraulic Pump':                  'hydraulic_pump',
    'Bucket':                          'bucket',
    'Other':                           'other',

    // Generic / legacy sentinel values
    'Cut Types':                       'REVIEW',
    'Cut Unit':                        'REVIEW',
    'Part':                            'REVIEW',
  },
};
